import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { formatDate as fmtDateShared } from '../utils/formatDate';

// PERSONAL developer tool — not linked from any nav/sidebar/permissions.
// Reachable only by navigating directly to /dev-console (admin-role gated
// in App.js). Lets any raw column of so_headers/so_line_items/
// invoice_headers/invoice_line_items be viewed, added, edited, or deleted
// directly, surfaces discrepancies (qty mismatches, missing invoices)
// automatically on load, and folds in the old ManualSOValidate.jsx
// dispatch-qty-entry flow as its own tab (same so-warehouse-scan.js
// endpoints, typed quantities instead of barcode scans).

const SO_HEADER_FIELDS = [
  'voucher_no', 'so_date', 'voucher_type', 'buyer', 'buyer_address', 'consignee',
  'consignee_address', 'place_of_supply', 'salesman', 'order_ref_no', 'gstin',
  'terms_of_payment', 'total_actual_qty', 'total_quantity', 'total_value',
  'gross_total', 'output_cgst', 'output_sgst', 'output_igst', 'round_off',
  'created_at', 'validation_status', 'validated_at', 'blob_url', 'pdf_extracted_at',
  'source', 'kam_validated', 'kam_validated_by', 'kam_validated_at', 'booking_type',
  'advance_booked_at', 'advance_booked_by', 'invoice_status', 'invoiced_at',
  'invoiced_by', 'boxes_packed', 'total_weight_kg'
];
const SO_LINE_FIELDS = [
  'so_voucher_no', 'ean', 'sku_name', 'place_of_supply', 'actual_qty', 'quantity',
  'rate', 'line_value', 'mrp', 'valid_till', 'kam_validated_qty', 'kam_variance'
];
const INVOICE_HEADER_FIELDS = [
  'invoice_no', 'invoice_date', 'order_ref_no', 'buyer', 'buyer_address', 'consignee',
  'consignee_address', 'total_quantity', 'taxable_amount', 'tax_amount', 'net_amount',
  'blob_url', 'pdf_extracted_at', 'source', 'created_at'
];
const INVOICE_LINE_FIELDS = [
  'invoice_no', 'sl_no', 'sku_name', 'ean', 'hsn', 'mrp', 'quantity', 'rate',
  'discount', 'taxable_amount', 'igst_rate', 'igst_amount', 'tax_type',
  'cgst_amount', 'cgst_rate', 'sgst_amount', 'sgst_rate'
];

function label(col) {
  return col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function formatExtractedAt(v) {
  if (!v) return 'Extracted —';
  const d = new Date(v);
  if (isNaN(d)) return 'Extracted —';
  return 'Extracted ' + fmtDateShared(v) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function fmtValue(v) {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}

export default function DevConsole({ user, onLogout }) {
  const [tab, setTab] = useState('discrepancies');

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.badge}>Developer tool — not linked anywhere</div>
          <h1 style={S.h1}>Developer Console</h1>
        </div>
        {onLogout && <button onClick={onLogout} style={S.btnGhost}>Log out</button>}
      </div>

      <div style={S.tabRow}>
        <button onClick={() => setTab('discrepancies')} style={tab === 'discrepancies' ? S.tabActive : S.tab}>Discrepancies</button>
        <button onClick={() => setTab('so_editor')} style={tab === 'so_editor' ? S.tabActive : S.tab}>SO Editor</button>
        <button onClick={() => setTab('invoice_editor')} style={tab === 'invoice_editor' ? S.tabActive : S.tab}>Invoice Editor</button>
        <button onClick={() => setTab('manual_validate')} style={tab === 'manual_validate' ? S.tabActive : S.tab}>Manual SO Validation</button>
        <button onClick={() => setTab('po_review')} style={tab === 'po_review' ? S.tabActive : S.tab}>PO Review</button>
        <button onClick={() => setTab('invoice_review')} style={tab === 'invoice_review' ? S.tabActive : S.tab}>Invoice Review</button>
        <button onClick={() => setTab('so_pdf_review')} style={tab === 'so_pdf_review' ? S.tabActive : S.tab}>SO PDF Review</button>
      </div>

      {tab === 'discrepancies' && <DiscrepanciesTab />}
      {tab === 'so_editor' && <EntityEditorTab
        title="SO Editor"
        idLabel="SO voucher no."
        detailAction="so_detail"
        idParam="voucher_no"
        headerFields={SO_HEADER_FIELDS}
        headerKey="voucher_no"
        headerUpdateAction="update_so_header"
        headerKeyField="voucher_no"
        lineFields={SO_LINE_FIELDS}
        lineParentField="so_voucher_no"
        lineUpdateAction="update_so_line"
        lineAddAction="add_so_line"
        lineDeleteAction="delete_so_line"
        allowAdd
      />}
      {tab === 'invoice_editor' && <EntityEditorTab
        title="Invoice Editor"
        idLabel="Invoice no."
        detailAction="invoice_detail"
        idParam="invoice_no"
        headerFields={INVOICE_HEADER_FIELDS}
        headerKey="invoice_no"
        headerUpdateAction="update_invoice_header"
        headerKeyField="invoice_no"
        lineFields={INVOICE_LINE_FIELDS}
        lineParentField="invoice_no"
        lineUpdateAction="update_invoice_line"
        lineAddAction="add_invoice_line"
        lineDeleteAction="delete_invoice_line"
      />}
      {tab === 'manual_validate' && <ManualValidateTab />}
      {tab === 'po_review' && <PoReviewTab />}
      {tab === 'invoice_review' && <InvoiceReviewTab />}
      {tab === 'so_pdf_review' && <SoPdfReviewTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Discrepancies tab — auto-loads on mount, no search/button needed   */
/* ------------------------------------------------------------------ */
function DiscrepanciesTab() {
  const [mismatches, setMismatches] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [mmRes, miRes] = await Promise.all([
          fetch(`${API_BASE}/acc-so-invoice-check?action=qty_mismatch`),
          fetch(`${API_BASE}/acc-so-invoice-check?action=missing_invoices`),
        ]);
        const mmData = await mmRes.json();
        const miData = await miRes.json();
        if (!mmRes.ok || !mmData.success) throw new Error(mmData.message || 'Failed to load qty mismatches');
        if (!miRes.ok || !miData.success) throw new Error(miData.message || 'Failed to load missing invoices');
        setMismatches(mmData.mismatches || []);
        setMissing(miData.missing || []);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    })();
  }, []);

  const formatDate = fmtDateShared;

  if (loading) return <div style={{ padding: 24, color: '#8C82A0', fontSize: 13 }}>Loading discrepancies…</div>;

  return (
    <>
      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}

      <div style={S.sectionTitle}>Qty Mismatches ({mismatches.length})</div>
      <div style={S.tableWrap}>
        <div style={{ ...S.tableHeaderRow, gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.9fr 0.9fr 0.9fr' }}>
          <div style={S.th}>SO No.</div>
          <div style={S.th}>PO No.</div>
          <div style={S.th}>Buyer</div>
          <div style={S.th}>SO Date</div>
          <div style={{ ...S.th, textAlign: 'right' }}>Header Qty</div>
          <div style={{ ...S.th, textAlign: 'right' }}>Line Sum</div>
          <div style={{ ...S.th, textAlign: 'right' }}>Missing</div>
        </div>
        {mismatches.map(m => (
          <div key={m.voucher_no} style={{ ...S.tableRow, gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.9fr 0.9fr 0.9fr' }}>
            <div style={S.td}>{m.voucher_no}</div>
            <div style={S.td}>{m.order_ref_no || '—'}</div>
            <div style={S.td}>{m.buyer || m.consignee || '—'}</div>
            <div style={S.td}>{formatDate(m.so_date)}</div>
            <div style={{ ...S.td, textAlign: 'right' }}>{m.total_quantity}</div>
            <div style={{ ...S.td, textAlign: 'right' }}>{m.line_qty_sum}</div>
            <div style={{ ...S.td, textAlign: 'right', color: '#C25E77', fontWeight: 700 }}>{m.total_quantity - m.line_qty_sum}</div>
          </div>
        ))}
        {mismatches.length === 0 && <div style={S.emptyRow}>No mismatches found.</div>}
      </div>

      <div style={{ ...S.sectionTitle, marginTop: 24 }}>Missing Invoices ({missing.length})</div>
      <div style={S.tableWrap}>
        <div style={S.tableHeaderRow}>
          <div style={S.th}>SO No.</div>
          <div style={S.th}>PO No.</div>
          <div style={S.th}>Buyer</div>
          <div style={S.th}>SO Date</div>
          <div style={{ ...S.th, textAlign: 'right' }}>Qty</div>
        </div>
        {missing.map(m => (
          <div key={m.voucher_no} style={S.tableRow}>
            <div style={S.td}>{m.voucher_no}</div>
            <div style={S.td}>{m.order_ref_no || '—'}</div>
            <div style={S.td}>{m.buyer || m.consignee || '—'}</div>
            <div style={S.td}>{formatDate(m.so_date)}</div>
            <div style={{ ...S.td, textAlign: 'right' }}>{m.total_quantity}</div>
          </div>
        ))}
        {missing.length === 0 && <div style={S.emptyRow}>No missing invoices found.</div>}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* PO Review — extractions from process-po.js that failed header-level */
/* validation (missing/implausible fields). Correct and insert into    */
/* po_headers, or reject.                                               */
/* ------------------------------------------------------------------ */
const PO_FIELDS = ['po_number', 'po_date', 'party_name', 'location', 'po_units', 'po_amount', 'po_expiry_date'];

function PoReviewTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const displayName = localStorage.getItem('display_name') || 'Admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/po-extraction-review?action=list&status=pending`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load');
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openItem(item) {
    setOpenId(item.id);
    setMsg('');
    let extracted = {};
    try { extracted = JSON.parse(item.extracted_json); } catch { /* leave empty */ }
    setForm(Object.fromEntries(PO_FIELDS.map(f => [f, extracted[f] ?? ''])));
  }

  async function approveAndInsert(id) {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/po-extraction-review?action=correct`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, corrected_json: form, reviewed_by: displayName }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save');
      setMsg('Inserted into po_headers.');
      setOpenId(null);
      load();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function reject(id) {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/po-extraction-review?action=reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, reviewed_by: displayName }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to reject');
      setMsg('Rejected.');
      setOpenId(null);
      load();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 24, color: '#8C82A0', fontSize: 13 }}>Loading pending PO reviews…</div>;

  return (
    <>
      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}
      {msg && <div style={{ ...S.banner, background: msg.startsWith('Error') ? '#FBF1F3' : '#EAFAF0', color: msg.startsWith('Error') ? '#C25E77' : '#158A45' }}>{msg}</div>}

      <div style={S.sectionTitle}>Pending PO Extractions ({items.length})</div>
      {items.length === 0 ? (
        <div style={S.emptyRow}>Nothing pending review.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(item => {
            let extracted = {};
            try { extracted = JSON.parse(item.extracted_json); } catch { /* ignore */ }
            let flags = [];
            try { flags = JSON.parse(item.validation_flags || '[]'); } catch { /* ignore */ }
            const isOpen = openId === item.id;
            return (
              <div key={item.id} style={S.tableWrap}>
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => isOpen ? setOpenId(null) : openItem(item)}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.po_file_name || `#${item.id}`}</div>
                      {item.doc_confidence === 'likely_real_po' && (
                        <span style={{ ...S.badge, margin: 0, background: '#E4F7EC', color: '#0F7B4F' }}>Likely real PO</span>
                      )}
                      {flags.includes('low_confidence_extraction') && (
                        <span style={{ ...S.badge, margin: 0, background: '#F5EDDC', color: '#9A7B3E' }}>Low confidence</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#8C82A0', marginTop: 2 }}>
                      {formatExtractedAt(item.created_at)} · {item.model_used} · {flags.join(', ') || 'no flags'} · {extracted.party_name || '—'}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#6E58A6', fontWeight: 600 }}>{isOpen ? 'Close' : 'Review'}</div>
                </div>
                {isOpen && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid #F4F1F8' }}>
                    <div style={S.formGrid}>
                      {PO_FIELDS.map(f => (
                        <div key={f}>
                          <div style={S.fieldLabel}>{label(f)}</div>
                          <input
                            type="text"
                            value={form[f] ?? ''}
                            onChange={e => setForm(prev => ({ ...prev, [f]: e.target.value }))}
                            style={S.formInput}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                      <button onClick={() => approveAndInsert(item.id)} disabled={saving} style={S.btnPrimary}>
                        {saving ? 'Saving…' : 'Approve & Insert'}
                      </button>
                      <button onClick={() => reject(item.id)} disabled={saving} style={{ ...S.btnGhost, color: '#C25E77' }}>
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Invoice Review — extractions from process-invoice.js that failed    */
/* header- or line-item-level validation (missing fields, sum mismatch */
/* between line items and printed header totals, bad EAN). Unlike PO   */
/* Review, this needs an editable line-item grid alongside the header  */
/* form since invoices always carry real SKU-level detail.             */
/* ------------------------------------------------------------------ */
const INVOICE_REVIEW_HEADER_FIELDS = [
  'invoice_no', 'invoice_date', 'order_ref_no', 'buyer', 'buyer_address',
  'consignee', 'consignee_address', 'total_quantity', 'taxable_amount',
  'tax_amount', 'net_amount', 'tax_type'
];
const INVOICE_REVIEW_LINE_FIELDS = [
  'sl_no', 'sku_name', 'ean', 'hsn', 'mrp', 'quantity', 'rate', 'discount',
  'taxable_amount', 'tax_type', 'igst_rate', 'igst_amount',
  'cgst_rate', 'cgst_amount', 'sgst_rate', 'sgst_amount'
];

function InvoiceReviewTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({});
  const [extraFields, setExtraFields] = useState({}); // non-form fields from extracted_json (blob_url, blob_name) — carried through untouched so approve doesn't lose them
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const displayName = localStorage.getItem('display_name') || 'Admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/invoice-extraction-review?action=list&status=pending`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load');
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openItem(item) {
    setOpenId(item.id);
    setMsg('');
    let extracted = {};
    try { extracted = JSON.parse(item.extracted_json); } catch { /* leave empty */ }
    setForm(Object.fromEntries(INVOICE_REVIEW_HEADER_FIELDS.map(f => [f, extracted[f] ?? ''])));
    setLineItems((extracted.line_items || []).map((li, i) => ({ ...li, _key: i })));
    // blob_url/blob_name aren't part of the editable form, but must survive
    // into corrected_json on save — otherwise the invoice's PDF link is lost
    // the moment it goes through review (see invoice-extraction-review.js).
    setExtraFields({ blob_url: extracted.blob_url ?? null, blob_name: extracted.blob_name ?? null });
  }

  // Expected tax for a line, derived from its own taxable_amount × rate —
  // used only to highlight what still needs a human's attention; the actual
  // auto-correction of this same formula now happens server-side in
  // process-invoice.js before an invoice would even reach this queue.
  function lineTaxMismatch(li) {
    const taxable = parseFloat(li.taxable_amount);
    if (!(taxable > 0)) return false;
    const TOL = 0.05;
    if (li.tax_type === 'IGST') {
      const rate = parseFloat(li.igst_rate);
      if (isNaN(rate)) return false;
      return Math.abs(Math.round(taxable * rate) / 100 - parseFloat(li.igst_amount || 0)) > TOL;
    }
    const cgstRate = parseFloat(li.cgst_rate);
    const sgstRate = parseFloat(li.sgst_rate);
    let mismatch = false;
    if (!isNaN(cgstRate)) mismatch = mismatch || Math.abs(Math.round(taxable * cgstRate) / 100 - parseFloat(li.cgst_amount || 0)) > TOL;
    if (!isNaN(sgstRate)) mismatch = mismatch || Math.abs(Math.round(taxable * sgstRate) / 100 - parseFloat(li.sgst_amount || 0)) > TOL;
    return mismatch;
  }

  const lineTotals = lineItems.reduce((acc, li) => {
    acc.qty     += parseFloat(li.quantity) || 0;
    acc.taxable += parseFloat(li.taxable_amount) || 0;
    acc.tax     += li.tax_type === 'IGST'
      ? (parseFloat(li.igst_amount) || 0)
      : (parseFloat(li.cgst_amount) || 0) + (parseFloat(li.sgst_amount) || 0);
    return acc;
  }, { qty: 0, taxable: 0, tax: 0 });

  function updateLineField(key, field, value) {
    setLineItems(prev => prev.map(l => l._key === key ? { ...l, [field]: value } : l));
  }

  function deleteLine(key) {
    setLineItems(prev => prev.filter(l => l._key !== key));
  }

  function addLine() {
    setLineItems(prev => [...prev, { _key: (prev.at(-1)?._key ?? -1) + 1, sl_no: prev.length + 1 }]);
  }

  async function approveAndInsert(id) {
    setSaving(true);
    setMsg('');
    try {
      const numFields = ['total_quantity', 'taxable_amount', 'tax_amount', 'net_amount'];
      const lineNumFields = ['sl_no', 'mrp', 'quantity', 'rate', 'discount', 'taxable_amount', 'igst_rate', 'igst_amount', 'cgst_rate', 'cgst_amount', 'sgst_rate', 'sgst_amount'];
      const corrected = { ...extraFields, ...form };
      numFields.forEach(f => { corrected[f] = form[f] === '' ? null : parseFloat(form[f]); });
      corrected.line_items = lineItems.map(({ _key, ...li }) => {
        const out = { ...li };
        lineNumFields.forEach(f => { out[f] = li[f] === '' || li[f] == null ? null : parseFloat(li[f]); });
        return out;
      });

      const res = await fetch(`${API_BASE}/invoice-extraction-review?action=correct`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, corrected_json: corrected, reviewed_by: displayName }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save');
      setMsg('Inserted into invoice_headers / invoice_line_items.');
      setOpenId(null);
      load();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function reject(id) {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/invoice-extraction-review?action=reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, reviewed_by: displayName }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to reject');
      setMsg('Rejected.');
      setOpenId(null);
      load();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 24, color: '#8C82A0', fontSize: 13 }}>Loading pending invoice reviews…</div>;

  return (
    <>
      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}
      {msg && <div style={{ ...S.banner, background: msg.startsWith('Error') ? '#FBF1F3' : '#EAFAF0', color: msg.startsWith('Error') ? '#C25E77' : '#158A45' }}>{msg}</div>}

      <div style={S.sectionTitle}>Pending Invoice Extractions ({items.length})</div>
      {items.length === 0 ? (
        <div style={S.emptyRow}>Nothing pending review.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(item => {
            let extracted = {};
            try { extracted = JSON.parse(item.extracted_json); } catch { /* ignore */ }
            let flags = [];
            try { flags = JSON.parse(item.validation_flags || '[]'); } catch { /* ignore */ }
            const isOpen = openId === item.id;
            return (
              <div key={item.id} style={S.tableWrap}>
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => isOpen ? setOpenId(null) : openItem(item)}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.invoice_file_name || `#${item.id}`}</div>
                    <div style={{ fontSize: 11.5, color: '#8C82A0', marginTop: 2 }}>
                      {flags.join(', ') || 'no flags'} · {extracted.buyer || '—'} · {(extracted.line_items || []).length} line items
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#6E58A6', fontWeight: 600 }}>{isOpen ? 'Close' : 'Review'}</div>
                </div>
                {isOpen && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid #F4F1F8' }}>
                    <div style={{ ...S.sectionTitle, marginTop: 16 }}>Header</div>
                    <div style={S.formGrid}>
                      {INVOICE_REVIEW_HEADER_FIELDS.map(f => (
                        <div key={f}>
                          <div style={S.fieldLabel}>{label(f)}</div>
                          <input
                            type="text"
                            value={form[f] ?? ''}
                            onChange={e => setForm(prev => ({ ...prev, [f]: e.target.value }))}
                            style={S.formInput}
                          />
                        </div>
                      ))}
                    </div>

                    <div style={{ ...S.sectionTitle, marginTop: 16 }}>Line Items ({lineItems.length})</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={S.rawTable}>
                        <thead>
                          <tr>
                            {INVOICE_REVIEW_LINE_FIELDS.map(f => <th key={f} style={S.rawTh}>{label(f)}</th>)}
                            <th style={S.rawTh}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map(l => {
                            const taxMismatch = lineTaxMismatch(l);
                            const taxFields = ['igst_amount', 'cgst_amount', 'sgst_amount'];
                            return (
                              <tr key={l._key}>
                                {INVOICE_REVIEW_LINE_FIELDS.map(f => (
                                  <td key={f} style={taxMismatch && taxFields.includes(f) ? { ...S.rawTd, background: '#FBF1F3' } : S.rawTd}>
                                    <input
                                      type="text"
                                      value={l[f] ?? ''}
                                      onChange={e => updateLineField(l._key, f, e.target.value)}
                                      style={taxMismatch && taxFields.includes(f) ? { ...S.rawInput, borderColor: '#C25E77', background: '#FBF1F3' } : S.rawInput}
                                    />
                                  </td>
                                ))}
                                <td style={S.rawTd}>
                                  <button onClick={() => deleteLine(l._key)} style={{ ...S.btnGhost, color: '#C25E77' }}>Delete</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ display: 'flex', gap: 24, marginTop: 10, fontSize: 12, color: '#8C82A0' }}>
                      <span>
                        Line qty sum: <strong style={{ color: lineTotals.qty === parseFloat(form.total_quantity) ? '#158A45' : '#C25E77' }}>{lineTotals.qty}</strong>
                        {' '}vs header {form.total_quantity || 0}
                      </span>
                      <span>
                        Line taxable sum: <strong style={{ color: Math.abs(lineTotals.taxable - parseFloat(form.taxable_amount || 0)) <= 0.10 ? '#158A45' : '#C25E77' }}>{lineTotals.taxable.toFixed(2)}</strong>
                        {' '}vs header {form.taxable_amount || 0}
                      </span>
                      <span>
                        Line tax sum: <strong style={{ color: Math.abs(lineTotals.tax - parseFloat(form.tax_amount || 0)) <= 0.10 ? '#158A45' : '#C25E77' }}>{lineTotals.tax.toFixed(2)}</strong>
                        {' '}vs header {form.tax_amount || 0}
                      </span>
                    </div>

                    <button onClick={addLine} style={{ ...S.btnGhost, marginTop: 10 }}>+ Add Line</button>

                    <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                      <button onClick={() => approveAndInsert(item.id)} disabled={saving} style={S.btnPrimary}>
                        {saving ? 'Saving…' : 'Approve & Insert'}
                      </button>
                      <button onClick={() => reject(item.id)} disabled={saving} style={{ ...S.btnGhost, color: '#C25E77' }}>
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Add New SO — manual entry modal. Fields mirror exactly what a real */
/* SO carries per process-so.js's own ingestion INSERTs.              */
/* ------------------------------------------------------------------ */
const ADD_SO_HEADER_FIELDS = [
  'voucher_no', 'so_date', 'buyer', 'buyer_address', 'consignee',
  'consignee_address', 'gstin', 'place_of_supply', 'order_ref_no', 'salesman',
];
const ADD_SO_LINE_FIELDS = ['ean', 'sku_name', 'quantity', 'mrp', 'valid_till'];

/* ------------------------------------------------------------------ */
/* SO PDF Review — SOs whose single-SO PDF (separate store            */
/* so-single-pdfs, backend/shared/soSinglePdf.js) couldn't be built   */
/* and verified. Their PDF button keeps opening the original file.    */
/* ------------------------------------------------------------------ */
const SO_PDF_ISSUES = {
  mismatch:       { label: 'Total mismatch',     color: '#C25E77', help: 'The "Total … nos" on this SO’s own pages doesn’t match the portal total — check the pages or the SO data.' },
  not_found:      { label: 'Voucher not in PDF', color: '#9A6400', help: 'This voucher number wasn’t found on any "SALES ORDER" page of the original file.' },
  missing_source: { label: 'Original missing',   color: '#6E58A6', help: 'The original PDF isn’t in storage — upload the correct single-SO PDF.' },
  error:          { label: 'Error',              color: '#B4232C', help: 'Something failed while reading the PDF — retry, or set the pages / upload manually.' },
};

function SoPdfReviewTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState([]);
  const [show, setShow] = useState('open');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [pagesInput, setPagesInput] = useState({});
  const displayName = localStorage.getItem('display_name') || 'Admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/so-single-pdf?action=review&show=${show}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error('Failed to load');
      setRows(data.rows || []);
      setCounts(data.counts || []);
    } catch (err) { setMsg('Error: ' + err.message); }
    setLoading(false);
  }, [show]);
  useEffect(() => { load(); }, [load]);

  async function post(action, row, extra = {}) {
    const key = `${row.voucher_no}|${row.so_source}`;
    setBusy(key); setMsg('');
    try {
      const res = await fetch(`${API_BASE}/so-single-pdf?action=${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voucher_no: row.voucher_no, so_source: row.so_source, updated_by: displayName, ...extra }),
      });
      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { data = { message: text }; }
      if (!res.ok || !data.success) throw new Error(data.message || text || 'Failed');
      if (action === 'retry') setMsg(data.status === 'ok' ? `${row.voucher_no}: verified and saved ✓` : `${row.voucher_no}: still ${data.status} — ${data.detail || ''}`);
      else if (action === 'resolve') setMsg(`${row.voucher_no}: marked resolved (keeps opening the original PDF).`);
      else { setMsg(`${row.voucher_no}: single-SO PDF saved ✓`); if (data.url) window.open(data.url, '_blank', 'noopener'); }
      load();
    } catch (err) { setMsg('Error: ' + err.message); }
    setBusy('');
  }

  function upload(row, file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => post('upload', row, { pdf_base64: String(reader.result).split(',')[1] });
    reader.readAsDataURL(file);
  }

  const openTotals = Object.fromEntries(counts.map(c => [c.status, c.open_n]));
  const issueTotal = Object.keys(SO_PDF_ISSUES).reduce((a, k) => a + (openTotals[k] || 0), 0);
  const okCount = counts.filter(c => c.status === 'ok' || c.status === 'manual').reduce((a, c) => a + c.n, 0);
  const visible = filter ? rows.filter(r => r.status === filter) : rows;

  return (
    <>
      {msg && <div style={{ ...S.banner, background: msg.startsWith('Error') ? '#FBF1F3' : '#EAFAF0', color: msg.startsWith('Error') ? '#C25E77' : '#158A45' }}>{msg}</div>}
      <div style={{ fontSize: 13, color: '#6B6379', marginBottom: 12, lineHeight: 1.5 }}>
        Each SO gets its own PDF in the separate <b>so-single-pdfs</b> store — its pages from the <b>SALES ORDER</b> header to its <b>Total … nos</b>, saved only when that total matches the portal.
        <b> {okCount.toLocaleString('en-IN')}</b> verified so far. The SOs below couldn’t be verified and still open their original PDF.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <button style={!filter ? S.tabActive : S.tab} onClick={() => setFilter('')}>All issues ({issueTotal})</button>
        {Object.entries(SO_PDF_ISSUES).map(([k, v]) => (
          <button key={k} style={filter === k ? S.tabActive : S.tab} onClick={() => setFilter(k)}>{v.label} ({openTotals[k] || 0})</button>
        ))}
        <span style={{ flex: 1 }} />
        <select value={show} onChange={e => setShow(e.target.value)} style={S.smallInput}>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <button style={S.btnGhost} onClick={load}>Refresh</button>
      </div>

      {loading ? <div style={{ padding: 24, color: '#8C82A0', fontSize: 13 }}>Loading…</div> : visible.length === 0 ? (
        <div style={S.emptyRow}>Nothing to review. 🎉</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(r => {
            const issue = SO_PDF_ISSUES[r.status] || { label: r.status, color: '#6B6379', help: '' };
            const key = `${r.voucher_no}|${r.so_source}`;
            return (
              <div key={key} style={{ border: '1px solid #ECE7F2', borderLeft: `4px solid ${issue.color}`, borderRadius: 10, padding: '12px 14px', background: '#fff' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b style={{ fontSize: 14 }}>{r.voucher_no}</b>
                  {r.so_source === 'cancelled' && <span style={{ fontSize: 11, color: '#6B6379', background: '#F1EEF6', padding: '2px 8px', borderRadius: 999 }}>cancelled SO</span>}
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: issue.color, background: '#FAF8FC', border: `1px solid ${issue.color}40`, padding: '2px 9px', borderRadius: 999 }}>{issue.label}</span>
                  <span style={{ fontSize: 12.5, color: '#4A4356' }}>PO {r.po_number || '—'} · {r.party || '—'} · {r.so_date || '—'}</span>
                  {r.resolved_at && <span style={{ fontSize: 11.5, color: '#158A45' }}>✓ resolved by {r.resolved_by} ({r.resolved_at})</span>}
                </div>
                <div style={{ fontSize: 12.5, color: '#4A4356', marginTop: 6 }}>
                  {r.detail}
                  {r.pages && <> · SO pages in original: <b>{r.pages}</b>{r.source_pages ? ` of ${r.source_pages}` : ''}</>}
                  {(r.pdf_total != null || r.db_total != null) && <> · PDF total <b>{r.pdf_total ?? '—'}</b> vs portal <b>{r.db_total ?? '—'}</b></>}
                </div>
                <div style={{ fontSize: 11.5, color: '#9A90AE', fontStyle: 'italic', marginTop: 2 }}>{issue.help}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                  {r.original_url && <a href={r.original_url} target="_blank" rel="noopener noreferrer" style={{ ...S.btnGhost, textDecoration: 'none' }}>Open original PDF</a>}
                  <button style={S.btnGhost} disabled={busy === key} onClick={() => post('retry', r)}>Retry</button>
                  {r.original_url && (
                    <>
                      <input style={{ ...S.smallInput, width: 110 }} placeholder="Pages e.g. 5-6" value={pagesInput[key] || ''} onChange={e => setPagesInput(p => ({ ...p, [key]: e.target.value }))} />
                      <button style={S.btnPrimary} disabled={busy === key || !(pagesInput[key] || '').trim()} onClick={() => post('set_pages', r, { pages: pagesInput[key] })}>Save these pages</button>
                    </>
                  )}
                  <label style={{ ...S.btnGhost, cursor: 'pointer' }}>
                    Upload correct PDF
                    <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => upload(r, e.target.files[0])} />
                  </label>
                  {!r.resolved_at && <button style={S.btnGhost} disabled={busy === key} onClick={() => post('resolve', r)}>Mark resolved</button>}
                  {busy === key && <span style={{ fontSize: 12, color: '#8C82A0' }}>Working…</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function emptyLine() { return { ean: '', sku_name: '', quantity: '', mrp: '', valid_till: '' }; }

function AddSoModal({ onClose, onCreated }) {
  const [form, setForm] = useState(Object.fromEntries(ADD_SO_HEADER_FIELDS.map(f => [f, ''])));
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [skuResults, setSkuResults] = useState({}); // row index -> search results
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totalQty = lineItems.reduce((sum, li) => sum + (parseFloat(li.quantity) || 0), 0);

  const canSave = ADD_SO_HEADER_FIELDS.every(f => form[f].trim()) &&
    lineItems.length > 0 &&
    lineItems.every(li => ADD_SO_LINE_FIELDS.every(f => String(li[f]).trim()));

  async function searchSku(idx, q) {
    updateLine(idx, 'sku_name', q);
    if (!q || q.trim().length < 2) { setSkuResults(prev => ({ ...prev, [idx]: [] })); return; }
    try {
      const res = await fetch(`${API_BASE}/gifting-dispatch?action=sku_lookup&q=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setSkuResults(prev => ({ ...prev, [idx]: data.skus || [] }));
    } catch { /* ignore search errors */ }
  }

  function pickSku(idx, sku) {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, ean: sku.ean_number, sku_name: sku.product_name } : li));
    setSkuResults(prev => ({ ...prev, [idx]: [] }));
  }

  function updateLine(idx, field, value) {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  }

  function addLineRow() { setLineItems(prev => [...prev, emptyLine()]); }
  function removeLineRow(idx) { setLineItems(prev => prev.filter((_, i) => i !== idx)); }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/dev-console?action=add_so_header`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: form, line_items: lineItems }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create SO');
      onCreated(data.voucher_no);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  return (
    <div style={S.modalOverlay}>
      <div style={S.modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Add New SO</div>
          <button onClick={onClose} style={S.btnGhost}>Close</button>
        </div>

        {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}

        <div style={S.sectionTitle}>Header</div>
        <div style={S.formGrid}>
          {ADD_SO_HEADER_FIELDS.map(f => (
            <div key={f}>
              <div style={S.fieldLabel}>{label(f)} *</div>
              <input
                type={f === 'so_date' ? 'date' : 'text'}
                value={form[f]}
                onChange={e => setForm(prev => ({ ...prev, [f]: e.target.value }))}
                style={S.formInput}
              />
            </div>
          ))}
        </div>

        <div style={{ ...S.sectionTitle, marginTop: 18 }}>Line Items ({lineItems.length}) — Total Qty: {totalQty}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.rawTable}>
            <thead>
              <tr>
                <th style={S.rawTh}>SKU Name</th>
                <th style={S.rawTh}>EAN</th>
                <th style={S.rawTh}>Quantity</th>
                <th style={S.rawTh}>MRP</th>
                <th style={S.rawTh}>Valid Till</th>
                <th style={S.rawTh}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, idx) => (
                <tr key={idx}>
                  <td style={{ ...S.rawTd, position: 'relative' }}>
                    <input
                      type="text"
                      value={li.sku_name}
                      onChange={e => searchSku(idx, e.target.value)}
                      style={S.rawInput}
                      placeholder="Search SKU/EAN…"
                    />
                    {skuResults[idx]?.length > 0 && (
                      <div style={S.skuDropdown}>
                        {skuResults[idx].map(s => (
                          <div key={s.ean_number} style={S.skuOption} onClick={() => pickSku(idx, s)}>
                            {s.product_name} <span style={{ color: '#8C82A0' }}>({s.ean_number})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={S.rawTd}>
                    <input type="text" value={li.ean} onChange={e => updateLine(idx, 'ean', e.target.value)} style={S.rawInput} />
                  </td>
                  <td style={S.rawTd}>
                    <input type="number" value={li.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} style={S.rawInput} />
                  </td>
                  <td style={S.rawTd}>
                    <input type="number" value={li.mrp} onChange={e => updateLine(idx, 'mrp', e.target.value)} style={S.rawInput} />
                  </td>
                  <td style={S.rawTd}>
                    <input type="date" value={li.valid_till} onChange={e => updateLine(idx, 'valid_till', e.target.value)} style={S.rawInput} />
                  </td>
                  <td style={S.rawTd}>
                    <button onClick={() => removeLineRow(idx)} style={{ ...S.btnGhost, color: '#C25E77' }}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addLineRow} style={{ ...S.btnGhost, marginTop: 10 }}>+ Add Line</button>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={save} disabled={!canSave || saving} style={{ ...S.btnPrimary, opacity: canSave ? 1 : 0.5 }}>
            {saving ? 'Saving…' : 'Save SO'}
          </button>
          <button onClick={onClose} style={S.btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Generic SO/Invoice raw editor — search, edit header, edit/add/     */
/* delete line items. Every column is editable (trusted admin tool).  */
/* ------------------------------------------------------------------ */
function EntityEditorTab({
  idLabel, detailAction, idParam, headerFields, headerUpdateAction, headerKeyField,
  lineFields, lineParentField, lineUpdateAction, lineAddAction, lineDeleteAction, allowAdd,
}) {
  const [idValue, setIdValue] = useState('');
  const [header, setHeader] = useState(null);
  const [headerForm, setHeaderForm] = useState({});
  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const load = useCallback(async (e, overrideId) => {
    e?.preventDefault?.();
    const targetId = (overrideId ?? idValue).trim();
    if (!targetId) return;
    setLoading(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/dev-console?action=${detailAction}&${idParam}=${encodeURIComponent(targetId)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Not found');
      setHeader(data.header);
      setHeaderForm(Object.fromEntries(headerFields.map(f => [f, fmtValue(data.header[f])])));
      setLines(data.line_items || []);
      setNewLine({});
    } catch (err) {
      setMsg('Error: ' + err.message);
      setHeader(null);
      setLines([]);
    }
    setLoading(false);
  }, [idValue, detailAction, idParam, headerFields]);

  async function saveHeader() {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/dev-console?action=${headerUpdateAction}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [headerKeyField]: header[headerKeyField], fields: headerForm }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      setMsg('Header saved.');
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function saveLine(line) {
    setSaving(true);
    setMsg('');
    try {
      const fields = Object.fromEntries(lineFields.map(f => [f, line[f]]));
      const res = await fetch(`${API_BASE}/dev-console?action=${lineUpdateAction}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.id, fields }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      setMsg(`Line ${line.id} saved.`);
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function deleteLine(id) {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/dev-console?action=${lineDeleteAction}&id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');
      setLines(prev => prev.filter(l => l.id !== id));
      setMsg(`Line ${id} deleted.`);
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function addLine() {
    setSaving(true);
    setMsg('');
    try {
      const fields = Object.fromEntries(lineFields.filter(f => f !== lineParentField).map(f => [f, newLine[f] || '']));
      const res = await fetch(`${API_BASE}/dev-console?action=${lineAddAction}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [lineParentField]: header[headerKeyField], fields }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Add failed');
      setMsg('Line added.');
      setNewLine({});
      load();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  function updateLineField(id, field, value) {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  }

  return (
    <>
      <form onSubmit={load} style={S.searchRow}>
        <input
          type="text"
          placeholder={idLabel}
          value={idValue}
          onChange={e => setIdValue(e.target.value)}
          style={S.searchInput}
        />
        <button type="submit" disabled={loading} style={S.btnPrimary}>{loading ? 'Loading…' : 'Load'}</button>
        {allowAdd && <button type="button" onClick={() => setShowAddModal(true)} style={S.btnGhost}>+ Add New SO</button>}
      </form>

      {showAddModal && (
        <AddSoModal
          onClose={() => setShowAddModal(false)}
          onCreated={(voucherNo) => {
            setShowAddModal(false);
            setIdValue(voucherNo);
            load(null, voucherNo);
          }}
        />
      )}

      {msg && <div style={{ ...S.banner, background: msg.startsWith('Error') ? '#FBF1F3' : '#EAFAF0', color: msg.startsWith('Error') ? '#C25E77' : '#158A45' }}>{msg}</div>}

      {header && (
        <>
          <div style={S.sectionTitle}>Header</div>
          <div style={S.formGrid}>
            {headerFields.map(f => (
              <div key={f}>
                <div style={S.fieldLabel}>{label(f)}</div>
                <input
                  type="text"
                  value={headerForm[f] ?? ''}
                  onChange={e => setHeaderForm(prev => ({ ...prev, [f]: e.target.value }))}
                  style={S.formInput}
                />
              </div>
            ))}
          </div>
          <button onClick={saveHeader} disabled={saving} style={{ ...S.btnPrimary, marginTop: 14, marginBottom: 24 }}>
            {saving ? 'Saving…' : 'Save Header'}
          </button>

          <div style={S.sectionTitle}>Line Items ({lines.length})</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.rawTable}>
              <thead>
                <tr>
                  {lineFields.map(f => <th key={f} style={S.rawTh}>{label(f)}</th>)}
                  <th style={S.rawTh}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id}>
                    {lineFields.map(f => (
                      <td key={f} style={S.rawTd}>
                        <input
                          type="text"
                          value={fmtValue(l[f])}
                          onChange={e => updateLineField(l.id, f, e.target.value)}
                          style={S.rawInput}
                        />
                      </td>
                    ))}
                    <td style={S.rawTd}>
                      <button onClick={() => saveLine(l)} disabled={saving} style={S.btnGhost}>Save</button>
                      <button onClick={() => deleteLine(l.id)} disabled={saving} style={{ ...S.btnGhost, color: '#C25E77', marginLeft: 6 }}>Delete</button>
                    </td>
                  </tr>
                ))}
                <tr>
                  {lineFields.filter(f => f !== lineParentField).map(f => (
                    <td key={f} style={S.rawTd}>
                      <input
                        type="text"
                        placeholder={label(f)}
                        value={newLine[f] || ''}
                        onChange={e => setNewLine(prev => ({ ...prev, [f]: e.target.value }))}
                        style={S.rawInput}
                      />
                    </td>
                  ))}
                  {lineFields.includes(lineParentField) && <td style={S.rawTd}></td>}
                  <td style={S.rawTd}>
                    <button onClick={addLine} disabled={saving} style={S.btnPrimary}>Add Line</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Manual SO Validation — folded in from the old ManualSOValidate.jsx */
/* Same so-warehouse-scan.js endpoints, typed quantities instead of   */
/* barcode scans.                                                     */
/* ------------------------------------------------------------------ */
function ManualValidateTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const [currentSO, setCurrentSO] = useState(null);
  const [lines, setLines] = useState([]);
  const [lineQtys, setLineQtys] = useState({});
  const [boxCount, setBoxCount] = useState('');
  const [totalWeight, setTotalWeight] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/so-warehouse-scan?action=pending&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load SOs');
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { loadList(); }, [loadList]);

  async function openSO(so) {
    setError('');
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/so-warehouse-scan?action=detail&voucher_no=${encodeURIComponent(so.voucher_no)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load SO detail');
      setCurrentSO(data.header);
      setLines(data.lines || []);
      const initQtys = {};
      (data.lines || []).forEach(l => {
        initQtys[l.id] = l.kam_validated_qty ?? l.po_qty ?? '';
      });
      setLineQtys(initQtys);
      setBoxCount('');
      setTotalWeight('');
    } catch (err) {
      setError(err.message);
    }
  }

  function closeSO() {
    setCurrentSO(null);
    setLines([]);
    setLineQtys({});
    setBoxCount('');
    setTotalWeight('');
    setMsg('');
  }

  async function handleSave() {
    if (!currentSO) return;
    setSaving(true);
    setMsg('');
    try {
      const line_quantities = lines.map(l => ({
        id: l.id,
        qty: lineQtys[l.id] !== undefined && lineQtys[l.id] !== '' ? parseFloat(lineQtys[l.id]) : 0,
      }));

      const res = await fetch(`${API_BASE}/so-warehouse-scan?action=validate_all`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voucher_no: currentSO.voucher_no,
          line_quantities,
          boxes_packed: parseInt(boxCount, 10) || 0,
          total_weight_kg: parseFloat(totalWeight) || 0,
          // This is a manual admin override — always honor the typed
          // line_quantities above, even for a box-mapping vendor (Type
          // 2/3) whose validate_all would otherwise derive actual_qty
          // from so_box_contents instead.
          manual: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to validate SO');

      setMsg(`SO ${currentSO.voucher_no} validated.`);
      closeSO();
      loadList();
    } catch (err) {
      setMsg('Error: ' + err.message);
    }
    setSaving(false);
  }

  return (
    <>
      {msg && <div style={{ ...S.banner, background: msg.startsWith('Error') ? '#FBF1F3' : '#EAFAF0', color: msg.startsWith('Error') ? '#C25E77' : '#158A45' }}>{msg}</div>}
      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}

      {!currentSO ? (
        <>
          <div style={S.searchRow}>
            <input
              type="text"
              placeholder="Search by SO number or party..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={S.searchInput}
            />
            <button onClick={loadList} style={S.btnGhost}>Refresh</button>
          </div>

          <div style={{ fontSize: 13, color: '#8C82A0', marginBottom: 10 }}>
            {loading ? 'Loading…' : `${list.length} pending SO${list.length !== 1 ? 's' : ''}`}
          </div>

          <div style={S.tableWrap}>
            <div style={S.tableHeaderRow}>
              <div style={S.th}>SO No.</div>
              <div style={S.th}>PO No.</div>
              <div style={S.th}>Buyer</div>
              <div style={S.th}>KAM</div>
              <div style={{ ...S.th, textAlign: 'right' }}>Qty</div>
              <div style={S.th}>KAM Approved</div>
              <div style={S.th}></div>
            </div>
            {list.map(so => (
              <div key={so.id} style={S.tableRow}>
                <div style={S.td}>{so.voucher_no}</div>
                <div style={S.td}>{so.order_ref_no || '—'}</div>
                <div style={S.td}>{so.buyer || so.consignee || '—'}</div>
                <div style={S.td}>{so.kam_name || '—'}</div>
                <div style={{ ...S.td, textAlign: 'right' }}>{so.total_quantity}</div>
                <div style={S.td}>{so.kam_validated ? '✓ Yes' : '— No'}</div>
                <div style={S.td}>
                  <button
                    onClick={() => so.kam_validated && openSO(so)}
                    disabled={!so.kam_validated}
                    style={{ ...S.btnPrimary, opacity: so.kam_validated ? 1 : 0.4, cursor: so.kam_validated ? 'pointer' : 'not-allowed' }}
                    title={so.kam_validated ? 'Validate this SO' : 'Not yet KAM-approved'}
                  >
                    Validate
                  </button>
                </div>
              </div>
            ))}
            {!loading && list.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: '#9A90AE' }}>No pending SOs.</div>
            )}
          </div>
        </>
      ) : (
        <div>
          <button onClick={closeSO} style={S.btnGhost}>&larr; Back to list</button>
          <div style={S.soHeader}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>SO {currentSO.voucher_no}</div>
            <div style={{ fontSize: 13, color: '#6B6379' }}>
              PO {currentSO.order_ref_no || '—'} &nbsp;·&nbsp; {currentSO.buyer || currentSO.consignee || '—'} &nbsp;·&nbsp; KAM: {currentSO.kam_name || '—'}
            </div>
          </div>

          <div style={S.tableWrap}>
            <div style={{ ...S.tableHeaderRow, gridTemplateColumns: '1.5fr 1fr 0.8fr 0.8fr 0.8fr 1fr' }}>
              <div style={S.th}>SKU</div>
              <div style={S.th}>EAN</div>
              <div style={{ ...S.th, textAlign: 'right' }}>PO Qty</div>
              <div style={{ ...S.th, textAlign: 'right' }}>KAM Qty</div>
              <div style={{ ...S.th, textAlign: 'right' }}>MRP</div>
              <div style={{ ...S.th, textAlign: 'right' }}>Dispatch Qty</div>
            </div>
            {lines.map(l => (
              <div key={l.id} style={{ ...S.tableRow, gridTemplateColumns: '1.5fr 1fr 0.8fr 0.8fr 0.8fr 1fr' }}>
                <div style={S.td}>{l.sku_name}</div>
                <div style={S.td}>{l.ean}</div>
                <div style={{ ...S.td, textAlign: 'right' }}>{l.po_qty}</div>
                <div style={{ ...S.td, textAlign: 'right' }}>{l.kam_validated_qty ?? '—'}</div>
                <div style={{ ...S.td, textAlign: 'right' }}>{l.mrp}</div>
                <div style={{ ...S.td, textAlign: 'right' }}>
                  <input
                    type="number"
                    value={lineQtys[l.id] ?? ''}
                    onChange={e => setLineQtys(prev => ({ ...prev, [l.id]: e.target.value }))}
                    style={S.qtyInput}
                  />
                </div>
              </div>
            ))}
          </div>

          <div style={S.footerRow}>
            <div>
              <div style={S.fieldLabel}>Boxes packed</div>
              <input type="number" min={0} value={boxCount} onChange={e => setBoxCount(e.target.value)} style={S.smallInput} />
            </div>
            <div>
              <div style={S.fieldLabel}>Total weight (kg)</div>
              <input type="number" step="0.01" min={0} value={totalWeight} onChange={e => setTotalWeight(e.target.value)} style={S.smallInput} />
            </div>
            <button onClick={handleSave} disabled={saving} style={{ ...S.btnPrimary, padding: '12px 28px', fontSize: 14 }}>
              {saving ? 'Saving…' : 'Validate SO'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const S = {
  page: { maxWidth: 1300, margin: '0 auto', padding: '28px 24px', fontFamily: "'Poppins', sans-serif", color: '#231C30' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 20, background: '#EDE9F7', color: '#5B4B8A', fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: '0.03em' },
  h1: { fontSize: 26, fontWeight: 700, margin: 0 },
  banner: { padding: '10px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 },
  tabRow: { display: 'flex', gap: 8, marginBottom: 18, borderBottom: '1px solid #ECE7F2' },
  tab: { padding: '10px 16px', border: 'none', background: 'none', color: '#8C82A0', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'", borderBottom: '2px solid transparent' },
  tabActive: { padding: '10px 16px', border: 'none', background: 'none', color: '#6E58A6', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'", borderBottom: '2px solid #6E58A6' },
  searchRow: { display: 'flex', gap: 10, marginBottom: 20 },
  searchInput: { flex: 1, padding: '10px 14px', borderRadius: 9, border: '1px solid #E4DEEE', fontSize: 13.5, fontFamily: "'Poppins'" },
  sectionTitle: { fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#231C30' },
  tableWrap: { borderRadius: 14, background: '#fff', border: '1px solid #ECE7F2', overflow: 'hidden' },
  tableHeaderRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.8fr 1fr 0.9fr', background: '#231C30', color: '#fff' },
  tableRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.8fr 1fr 0.9fr', borderBottom: '1px solid #F4F1F8', alignItems: 'center' },
  th: { padding: '12px 14px', fontSize: 11, fontWeight: 600, letterSpacing: '0.03em' },
  td: { padding: '12px 14px', fontSize: 13 },
  emptyRow: { padding: 24, textAlign: 'center', color: '#9A90AE', fontSize: 13 },
  qtyInput: { width: 80, padding: '6px 8px', borderRadius: 7, border: '1px solid #E4DEEE', fontSize: 13, textAlign: 'right', fontFamily: "'Poppins'" },
  smallInput: { width: 120, padding: '8px 12px', borderRadius: 8, border: '1px solid #E4DEEE', fontSize: 13, fontFamily: "'Poppins'" },
  fieldLabel: { fontSize: 11.5, fontWeight: 600, color: '#8C82A0', marginBottom: 4 },
  footerRow: { display: 'flex', alignItems: 'flex-end', gap: 20, marginTop: 18 },
  soHeader: { margin: '16px 0' },
  btnGhost: { padding: '9px 16px', borderRadius: 9, border: '1px solid #E4DEEE', background: '#fff', color: '#231C30', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Poppins'" },
  btnPrimary: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Poppins'" },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, background: '#fff', border: '1px solid #ECE7F2', borderRadius: 14, padding: 16 },
  formInput: { width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #E4DEEE', fontSize: 12.5, fontFamily: "'Poppins'", boxSizing: 'border-box' },
  rawTable: { borderCollapse: 'collapse', width: '100%', background: '#fff', border: '1px solid #ECE7F2', borderRadius: 14 },
  rawTh: { padding: '10px 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em', background: '#231C30', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap' },
  rawTd: { padding: '6px 8px', borderBottom: '1px solid #F4F1F8', whiteSpace: 'nowrap' },
  rawInput: { width: 100, padding: '5px 7px', borderRadius: 6, border: '1px solid #E4DEEE', fontSize: 12, fontFamily: "'Poppins'" },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modalBox: { background: '#fff', borderRadius: 16, padding: 24, maxWidth: 1000, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  skuDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #E4DEEE', borderRadius: 8, boxShadow: '0 6px 20px rgba(35,28,48,0.15)', zIndex: 10, maxHeight: 200, overflowY: 'auto' },
  skuOption: { padding: '8px 12px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #F4F1F8' },
};
